import { useEffect } from 'react';
import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

type InfoStyle = 'info' | 'warning' | 'tip' | 'success' | 'neutral';

interface InfoConfig {
  mediaUrl?: string;
  mediaType?: 'image' | 'video' | 'audio';
  infoStyle?: InfoStyle;
  showIcon?: boolean;
  content?: string;
  contentType?: 'text' | 'html' | 'markdown';
}

const styleConfig: Record<InfoStyle, { bg: string; border: string; icon: string; iconColor: string }> = {
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    iconColor: 'text-blue-500',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    iconColor: 'text-amber-500',
  },
  tip: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    iconColor: 'text-purple-500',
  },
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    iconColor: 'text-green-500',
  },
  neutral: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    iconColor: 'text-gray-500',
  },
};

/**
 * Info/Instruction question component.
 * Displays informational text with no response required.
 * Supports multiple display styles and media content.
 */
export function Info({
  question,
  value,
  onChange,
  errors,
}: QuestionComponentProps<string | null>) {
  const config = question.config as InfoConfig | undefined;

  // Mark as viewed automatically
  useEffect(() => {
    if (value !== 'viewed') {
      onChange('viewed');
    }
  }, [value, onChange]);

  // Config options
  const mediaUrl = config?.mediaUrl;
  const mediaType = config?.mediaType || 'image';
  const infoStyle: InfoStyle = config?.infoStyle || 'info';
  const showIcon = config?.showIcon ?? true;
  const customContent = config?.content;
  const contentType = config?.contentType || 'text';

  const style = styleConfig[infoStyle] || styleConfig.info;

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="space-y-4">
        {/* Optional media content */}
        {mediaUrl && (
          <div className="rounded-lg overflow-hidden">
            {mediaType === 'image' && (
              <img
                src={mediaUrl}
                alt="Information"
                className="max-h-96 w-full object-contain bg-gray-100"
              />
            )}
            {mediaType === 'video' && (
              <video
                src={mediaUrl}
                controls
                playsInline
                className="w-full"
              >
                Your browser does not support the video tag.
              </video>
            )}
            {mediaType === 'audio' && (
              <audio src={mediaUrl} controls className="w-full">
                Your browser does not support the audio element.
              </audio>
            )}
          </div>
        )}

        {/* Custom content (if provided) */}
        {customContent && (
          <div className="prose prose-sm max-w-none">
            {contentType === 'html' ? (
              <div dangerouslySetInnerHTML={{ __html: customContent }} />
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap">{customContent}</p>
            )}
          </div>
        )}

        {/* Styled info box */}
        <div className={cn(
          'flex items-start gap-3 rounded-lg border p-4',
          style.bg,
          style.border
        )}>
          {showIcon && (
            <svg
              className={cn('h-5 w-5 flex-shrink-0 mt-0.5', style.iconColor)}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={style.icon} />
            </svg>
          )}
          <div className="flex-1">
            <p className={cn(
              'text-sm',
              infoStyle === 'warning' && 'text-amber-800',
              infoStyle === 'info' && 'text-blue-800',
              infoStyle === 'tip' && 'text-purple-800',
              infoStyle === 'success' && 'text-green-800',
              infoStyle === 'neutral' && 'text-gray-700'
            )}>
              {infoStyle === 'tip' && <strong>Tip: </strong>}
              {infoStyle === 'warning' && <strong>Note: </strong>}
              Please read the information above, then continue to the next question.
            </p>
          </div>
        </div>
      </div>
    </QuestionWrapper>
  );
}
